<template>
  <BaseSimpleLayout
    class="ship-list"
    :sidebar-visible="sidebarVisible"
    :class="{ 'sidebar-hidden': !sidebarVisible }"
  >
    <template #sidebar>
      <SearchBar
        :filter-form="filterForm"
        :business-type-list="businessTypeList"
        :is-collapsed="!sidebarVisible"
        @update:filter-form="filterForm = $event"
        @close="toggleSidebar"
        @filter="filterShip"
        @filter-status="filterShipStatus"
        @filter-approve="filterShipApprove"
      />
    </template>
    <template #title>
      <div class="ship-list-print-title-block">
        <div class="ship-list-print-page-title-row">
          <div class="title-with-toggle">
            <span>出荷一覧</span>
            <el-button
              v-if="!sidebarVisible"
              class="sidebar-toggle-btn"
              type="primary"
              @click="toggleSidebar"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                  stroke="#fff"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </el-button>
          </div>
          <div
            v-if="loadingFlag"
            class="ship-list-print-export-date ship-list-print-export-date--header"
          >
            出力日: {{ printExportDate }}
          </div>
        </div>
        <div v-if="loadingFlag" class="ship-list-print-title-stats">
          <span class="ship-list-print-title-stat"
            >USS件数：<span class="count-strong">{{
              USSOrderCount
            }}</span></span
          >
          <span class="ship-list-print-title-stat"
            >GMS件数：<span class="count-strong">{{ orderCount }}</span></span
          >
          <span class="ship-list-print-title-stat"
            >東海理化件数：<span class="count-strong">{{
              tokarikaCount
            }}</span></span
          >
        </div>
      </div>
    </template>
    <template v-if="loadingFlag" #button-area>
      <ScreenPermissionGate code="SHIPPING-BTN_APPROVE_SHIPMENT_BULK">
        <el-button
          class="bulk-approve-btn"
          type="primary"
          size="large"
          :disabled="!canBulkApprove"
          @click="bulkApproveDialog = true"
        >
          一括出荷承認
        </el-button>
      </ScreenPermissionGate>
      <el-button size="large" type="primary" @click="handlePrint">
        印刷
      </el-button>
    </template>
    <template v-if="loadingFlag" #main>
      <el-form :model="filterForm" class="filter-form-horizontal">
        <el-form-item label="フィルター">
          <el-input
            v-model="filterForm.filterText"
            placeholder="フィルター"
            style="width: 200px"
            clearable
            @input="filterShip"
          />
        </el-form-item>
        <el-form-item label="フィルター2">
          <el-input
            v-model="filterForm.filterText2"
            placeholder="フィルター2"
            style="width: 200px"
            clearable
            @input="filterShip"
          />
        </el-form-item>
        <el-form-item label="出荷ID">
          <el-input
            v-model="filterForm.shipId"
            style="width: 100px"
            clearable
            placeholder="検索"
            @input="filterShip"
          />
        </el-form-item>
        <el-form-item label="MCCS Ver">
          <el-select
            v-model="filterForm.mccsVers"
            multiple
            collapse-tags
            collapse-tags-tooltip
            class="form-mccs-ver"
            placeholder="選択"
            @change="filterShip"
          >
            <el-option
              v-for="ver in mccsVerOptions"
              :key="ver"
              :label="ver"
              :value="ver"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="件数">
          <el-input-number
            class="form-count"
            v-model="filterForm.count"
            :min="1"
            :max="100000"
            :controls="false"
            :precision="0"
            @change="filterShip"
          />
        </el-form-item>
      </el-form>

      <div class="filter-display">
        <div class="disp-count">
          <span class="count-item"
            >USS件数：<span style="font-weight: bold">{{
              USSOrderCount
            }}</span></span
          >
          <span class="count-item"
            >GMS件数：<span style="font-weight: bold">{{
              orderCount
            }}</span></span
          >
          <span class="count-item"
            >東海理化件数：<span style="font-weight: bold">{{
              tokarikaCount
            }}</span></span
          >
        </div>
        <div class="toggle-section">
          <el-switch
            v-model="showAdditionalInfo"
            active-text="追加情報を表示する"
          />
        </div>
        <div class="business-type-legend">
          <div class="legend-item">
            <span class="color-box color-fintech"></span>
            <span>金融</span>
          </div>
          <div class="legend-item">
            <span class="color-box color-carsecurity"></span>
            <span>カーセキュリティ</span>
          </div>
          <div class="legend-item">
            <span class="color-box color-secual"></span>
            <span>セキュアル</span>
          </div>
          <div class="legend-item">
            <span class="color-box color-tokarika"></span>
            <span>東海理化</span>
          </div>
        </div>
      </div>
      <el-table
        ref="shipTableRef"
        :data="tableData"
        row-key="id"
        stripe
        border
        :max-height="tableHeight"
        empty-text="データが存在しません。"
        :row-class-name="getRowClassName"
        @selection-change="handleSelectionChange"
        @row-click="rowClick"
      >
        <el-table-column
          v-if="hasBulkApproveShipmentPermission"
          type="selection"
          width="48"
          :selectable="isRowSelectable"
        />
        <el-table-column
          property="id"
          label="ID"
          align="center"
          width="80"
          sortable
        />
        <el-table-column
          property="businessType"
          label="事業区分"
          align="center"
          width="130"
          sortable
        >
          <template #default="scope">
            {{ shipBusinessTypeToLabel(scope.row, businessTypeLabels) }}
          </template>
        </el-table-column>
        <el-table-column
          property="type"
          label="出荷種別"
          align="center"
          width="110"
          sortable
        >
          <template #default="scope">
            {{ replaceShipType(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column
          property="customerCode"
          label="顧客管理番号"
          align="center"
          width="140"
          sortable
          v-if="showAdditionalInfo"
        >
          <template #default="scope">
            {{ scope.row.customerCode ?? '-' }}
          </template>
        </el-table-column>
        <el-table-column
          property="orderCompanyName"
          label="確定端末"
          align="center"
          width="150"
          sortable
        >
          <template #default="scope">
            {{ scope.row.orderCompanyName ?? '-' }}
          </template>
        </el-table-column>
        <el-table-column
          property="deviceType"
          label="MCCS Ver"
          align="center"
          width="120"
          sortable
        >
          <template #default="scope">
            {{ mccsDeviceType?.[scope.row.deviceType] || '-' }}
          </template>
        </el-table-column>
        <el-table-column
          property="deviceSerialCode"
          label="シリアル番号"
          align="center"
          width="150"
          sortable
        >
          <template #default="scope">
            {{ replaceSerial(scope.row.deviceSerialCode) }}
          </template>
        </el-table-column>
        <el-table-column
          property="acceptDate"
          label="受注日"
          align="center"
          width="100"
          sortable
        >
          <template #default="scope">
            {{ convertDate(scope.row.acceptDate) }}
          </template>
        </el-table-column>
        <el-table-column
          property="shipDate"
          label="出荷日"
          align="center"
          width="100"
          sortable
          v-if="showAdditionalInfo"
        >
          <template #default="scope">
            {{ convertDate(scope.row.shipDate) }}
          </template>
        </el-table-column>
        <el-table-column
          property="arrivalDate"
          label="着指定日"
          align="center"
          width="110"
          sortable
        >
          <template #default="scope">
            {{ convertDate(scope.row.arrivalDate) }}
          </template>
        </el-table-column>
        <el-table-column
          property="invoiceCode"
          label="送り状番号"
          header-align="center"
          align="center"
          min-width="200"
          sortable
        >
          <template #default="scope">
            <div class="invoice-supplier-icon">
              <img
                v-if="scope.row.invoiceSupplier === INVOICE_SUPPLIER.YAMATO"
                src="@/assets/yamato.png"
                alt="Yamato"
                height="32"
              />
              <img
                v-else-if="
                  scope.row.invoiceSupplier === INVOICE_SUPPLIER.SAGAWA
                "
                src="@/assets/sagawa.png"
                alt="Sagawa"
                height="24"
              />
              <span class="invoice-code">{{ scope.row.invoiceCode }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          property="shipName"
          label="出荷先名"
          header-align="center"
          min-width="150"
          sortable
        />
        <el-table-column
          property="zip"
          label="出荷先住所"
          header-align="center"
          min-width="150"
          sortable
        >
          <template #default="scope">
            {{ replaceZip(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column
          property="status"
          label="ステータス"
          align="center"
          width="120"
          sortable
        >
          <template #default="scope">
            {{ shipStatusLabel[scope.row.status] }}
          </template>
        </el-table-column>
        <el-table-column
          property="isApproved"
          label="承認"
          align="center"
          width="80"
          sortable
        >
          <template #default="scope">
            {{ shipApprove[scope.row.isApproved ? 1 : 0] }}
          </template>
        </el-table-column>
      </el-table>
      <ShipListPrintTable
        class="ship-list-print-only"
        :rows="tableData"
        :business-type-labels="businessTypeLabels"
      />
      <el-dialog v-model="bulkApproveDialog" title="一括出荷承認" width="450px">
        <span>
          <b>選択した出荷を一括承認しますか？</b>
        </span>
        <template #footer>
          <el-button size="large" type="primary" @click="submitBulkApprove()">
            承認
          </el-button>
          <el-button size="large" @click="bulkApproveDialog = false">
            キャンセル
          </el-button>
        </template>
      </el-dialog>
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { ref, onBeforeUnmount, computed } from 'vue';
  import { useStore } from 'vuex';
  import router from '@/router';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import ScreenPermissionGate from '@/components/ScreenPermissionGate.vue';
  import { hasScreenCodePermission } from '@/libs/auth';
  import { ShipsDataState, ShipFilterData } from '@/types/ship';
  import { formatDate, addDay } from '@/libs/dateFormat';
  import {
    shipStatus as shipStatusLabel,
    shipType,
    shipBusinessTypeToLabel,
    shipApprove,
    INVOICE_SUPPLIER
  } from '@/libs/ship';
  import { mccsDeviceType } from '@/libs/device';
  import { ElLoading } from 'element-plus';
  import SearchBar from './components/SearchBar.vue';
  import ShipListPrintTable from './components/ShipListPrintTable.vue';
  import { BusinessTypesDataState } from '@/types/master/businessType';
  import { NumberData } from '@/types/common';
  import { BusinessType } from '@/libs/businessType';
  onBeforeUnmount(() => {
    clearInterval(reload);
  });

  const shipStatus = {
    request: 1,
    response: 2,
    complete: 3
  } as const;

  const mccsVerOptions = Object.values(mccsDeviceType);
  const defaultMccsVers = [...mccsVerOptions];

  const store = useStore();
  const tableData = ref<ShipsDataState[]>([]);
  const showAdditionalInfo = ref(false);
  const filterForm = ref<ShipFilterData>({
    acceptStartDate: null,
    acceptEndDate: null,
    shipStartDate: null,
    shipEndDate: null,
    filterText: null,
    filterText2: null,
    acceptId: null,
    shipId: null,
    status: ['依頼', '準備中'],
    shipType: ['出荷 ', '出荷(確定)', 'バルク'],
    businessTypes: [],
    mccsVers: [...defaultMccsVers],
    approveTypes: [''],
    count: 100
  });
  const printExportDate = formatDate(Date.now());

  const loadingFlag = ref(false);
  const tableHeight = ref(550);
  const sidebarVisible = ref(true);
  const bulkApproveDialog = ref(false);
  const selectedRows = ref<ShipsDataState[]>([]);
  const businessTypeList = ref<BusinessTypesDataState[]>([]);
  const businessTypeLabels = ref<NumberData | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shipTableRef = ref<any>(null);
  let USSOrderCount: number;
  let orderCount: number;
  let tokarikaCount: number;

  const resetSelection = () => {
    selectedRows.value = [];
    shipTableRef.value?.clearSelection?.();
  };

  const initialize = async () => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    if (store.state.ship.filterData) {
      filterForm.value = {
        ...filterForm.value,
        ...store.state.ship.filterData,
        mccsVers:
          store.state.ship.filterData.mccsVers ??
          store.state.ship.filterData.mccsVerTypes ??
          defaultMccsVers
      };
    }

    await Promise.all([
      store.dispatch('businessTypesMaster/fetch'),
      store.dispatch('ship/fetch', {
        status: getFilterStatusIds(),
        isApproved: filterForm.value.approveTypes.length > 1 ? true : false
      })
    ]);
    businessTypeList.value = store.state.businessTypesMaster.listData;
    businessTypeLabels.value = store.state.businessTypesMaster.replaceData;
    if (filterForm.value.businessTypes.length === 0) {
      filterForm.value.businessTypes = businessTypeList.value.map(
        (businessType) => businessType.id
      );
    }
    filterShip();
    resetSelection();
    tableHeight.value = window.innerHeight - 250;
    loadingInstance.close();
    loadingFlag.value = true;
  };

  const getFilterStatusIds = () => {
    return filterForm.value.status.map((data) => {
      return Object.entries(shipStatusLabel).find(
        (value) => value[1] === data
      )?.[0];
    });
  };

  const convertDate = (date: string | null) => {
    if (!date) {
      return '-';
    }
    return formatDate(Number(date));
  };

  const replaceSerial = (serialList: string[]) => {
    let serialData = '-';
    if (serialList.length === 1) {
      serialData = serialList[0].toString();
    }
    if (serialList.length > 1) {
      serialData = 'リンク済';
    }
    return serialData;
  };

  const replaceShipType = (data: ShipsDataState) => {
    let retunValue = shipType[data.type];
    if (data.type === 1) {
      const addInfo = data.orderCompanyId ? '(確定)' : ' ';
      retunValue = retunValue + addInfo;
    }
    return retunValue;
  };

  const replaceZip = (data: ShipsDataState) => {
    let zip = data.shipPrefecture + data.shipCity + data.shipAddress;
    if (data.shipAddressSub) zip = zip + data.shipAddressSub;
    return zip;
  };

  const filterShipStatus = async () => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    await store.dispatch('ship/fetch', {
      status: getFilterStatusIds(),
      isApproved: filterForm.value.approveTypes.length > 1 ? true : false
    });
    filterShip();
    loadingInstance.close();
    loadingFlag.value = true;
  };

  const filterShipApprove = async () => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    await store.dispatch('ship/fetch', {
      status: getFilterStatusIds(),
      isApproved: filterForm.value.approveTypes.length > 1 ? true : false
    });
    filterShip();
    loadingInstance.close();
    loadingFlag.value = true;
  };

  const filterShip = () => {
    let count = 0;
    USSOrderCount = 0;
    orderCount = 0;
    tokarikaCount = 0;
    store.dispatch('ship/setFilterData', filterForm.value);
    tableData.value = store.state.ship.listData.filter(
      (data: ShipsDataState) => {
        const customerCode = data.customerCode ?? '-';
        if (count >= filterForm.value.count) return false;
        if (!filterDate(data)) return false;
        if (
          filterForm.value.shipType &&
          !filterForm.value.shipType.includes(replaceShipType(data))
        )
          return false;

        if (!filterForm.value.businessTypes.includes(data.businessType))
          return false;

        const deviceTypeLabel = mccsDeviceType?.[data.deviceType ?? 0] || '-';
        if (!filterForm.value.mccsVers.includes(deviceTypeLabel)) return false;

        if (filterForm.value.approveTypes.length > 1 && !data.isApproved) {
          return false;
        }
        if (filterForm.value.approveTypes.length === 1 && data.isApproved) {
          return false;
        }
        if (
          filterForm.value.shipId &&
          !data.id.toString().includes(filterForm.value.shipId)
        )
          return false;
        if (
          filterForm.value.acceptId &&
          !data.acceptId.toString().includes(filterForm.value.acceptId)
        )
          return false;
        if (!filterTextData(data, 'filterText')) return false;
        if (!filterTextData(data, 'filterText2')) return false;

        // Count by customer code and business type

        if (data.businessType === BusinessType.TOKAI_RIKA) {
          ++tokarikaCount;
        } else if (customerCode.substring(0, 3) === 'USS') {
          ++USSOrderCount;
        } else {
          ++orderCount;
        }

        count += 1;
        return true;
      }
    );
  };

  const filterDate = (data: ShipsDataState): boolean => {
    let acceptFromResult = !filterForm.value.acceptStartDate;
    let acceptToResult = !filterForm.value.acceptEndDate;
    let shipFromResult = !filterForm.value.shipStartDate;
    let shipToResult = !filterForm.value.shipEndDate;
    if (filterForm.value.acceptStartDate && data.acceptDate) {
      acceptFromResult = filterForm.value.acceptStartDate <= data.acceptDate;
    }
    if (filterForm.value.acceptEndDate && data.acceptDate) {
      acceptToResult =
        data.acceptDate < addDay(filterForm.value.acceptEndDate, 1);
    }
    if (filterForm.value.shipStartDate && data.shipDate) {
      shipFromResult = filterForm.value.shipStartDate <= data.shipDate;
    }
    if (filterForm.value.shipEndDate && data.shipDate) {
      shipToResult = data.shipDate < addDay(filterForm.value.shipEndDate, 1);
    }
    return acceptFromResult && acceptToResult && shipFromResult && shipToResult;
  };

  const filterTextData = (
    data: ShipsDataState,
    text: 'filterText' | 'filterText2'
  ): boolean => {
    if (!filterForm.value[text]) return true;
    const value = filterForm.value[text] ?? '';
    const orderCompanyName = data.orderCompanyName ?? '-';
    const zip = replaceZip(data);
    const serialCode = replaceSerial(data.deviceSerialCode);
    const customerCode = data.customerCode ?? '-';
    return (
      orderCompanyName.includes(value) ||
      zip.includes(value) ||
      serialCode.includes(value) ||
      customerCode.includes(value) ||
      data.shipName.includes(value)
    );
  };

  const toggleSidebar = () => {
    sidebarVisible.value = !sidebarVisible.value;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSelectionChange = (rows: ShipsDataState[]) => {
    selectedRows.value = rows;
  };

  const eligibleSelectedIds = computed<number[]>(() => {
    return selectedRows.value.filter(isRowSelectable).map((r) => r.id);
  });

  const canBulkApprove = computed(() => {
    return eligibleSelectedIds.value.length > 0;
  });

  /** el-table-column must be direct children of el-table; cannot wrap in ScreenPermissionGate. */
  const hasBulkApproveShipmentPermission = computed(() => {
    return hasScreenCodePermission(
      'SHIPPING-BTN_APPROVE_SHIPMENT_BULK',
      'VIEW'
    );
  });

  const isRowSelectable = (row: ShipsDataState) => {
    return row.isApproved === false && row.status === shipStatus.complete;
  };

  const submitBulkApprove = async () => {
    const ids = eligibleSelectedIds.value;
    if (ids.length < 1) return;
    await store.dispatch('ship/approveBulk', { ids });
    bulkApproveDialog.value = false;
    await initialize();
    resetSelection();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rowClick = (row: ShipsDataState, column: any) => {
    if (column?.type === 'selection') return;
    router.push('/ship/' + row.id);
  };

  const getRowClassName = ({ row }: { row: ShipsDataState }) => {
    switch (row.businessType) {
      case BusinessType.SEQUAL:
        return 'row-secual';
      case BusinessType.TOKAI_RIKA:
        return 'row-tokarika';
      case BusinessType.CAR_SECURITY:
        return 'row-carsecurity';
      case BusinessType.FINANCE:
        return 'row-finance';
      default:
        return '';
    }
  };

  initialize();
  const reload = setInterval(initialize, 1000 * 60 * 3);
</script>
<style lang="scss">
  .ship-list {
    // Business type color variables
    --color-finance: #ffffff;
    --color-carsecurity: #fff0cf;
    --color-secual: #cde3f2;
    --color-tokarika: #d9e9d5;

    .main-container {
      width: 98%;
    }
    tbody {
      font-size: 13px;
      font-weight: 500;
    }
    thead {
      font-size: 14px;
    }
    .filter-form-horizontal {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
    }
    .el-form-item {
      text-align: left;
      margin-bottom: 10px;
      margin-right: 0;
      .el-form-item__label {
        font-weight: 600;
      }
    }
    .form-count {
      width: 90px;
      .el-input {
        width: 90px;
      }
    }
    .form-mccs-ver {
      width: 160px;
    }
    .el-checkbox {
      font-weight: 600;
    }
    .el-table {
      margin: 10px auto 0;
      thead {
        th {
          background: #f5f7fa;
        }
      }
    }
    .disp-count {
      text-align: left;
      display: flex;
      gap: 24px;
    }
    .count-item {
      white-space: nowrap;
    }
    .filter-display {
      display: flex;
      align-items: center;
      gap: 20px;
      margin-bottom: 10px;
      padding: 8px 0;
    }
    :deep(.header-container) {
      align-items: center;
      height: auto;
      min-height: 40px;
    }
    :deep(.page-title) {
      width: auto;
      flex: 1;
      min-width: 0;
    }
    :deep(.header-button-area) {
      width: auto;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
    }
    .toggle-section {
      display: flex;
      align-items: center;
    }
    .business-type-legend {
      display: flex;
      gap: 16px;
      align-items: center;
      margin-left: auto;
    }
    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 500;
    }
    .color-box {
      width: 20px;
      height: 20px;
      border: 1px solid #dcdfe6;
      border-radius: 3px;
    }
    .color-box.color-fintech {
      background-color: var(--color-finance);
    }
    .color-box.color-carsecurity {
      background-color: var(--color-carsecurity);
    }
    .color-box.color-secual {
      background-color: var(--color-secual);
    }
    .color-box.color-tokarika {
      background-color: var(--color-tokarika);
    }
    .ship-list-print-export-date--header {
      display: none;
    }

    .title-with-toggle {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .sidebar-toggle-btn {
      position: absolute;
      left: 20px;
      top: 67px;
      height: 32px;
      width: 24px;
      z-index: 10;
    }
    :deep(.base-simple-layout.sidebar-hidden .sidebar-container) {
      width: 0 !important;
    }

    .invoice-supplier-icon {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      gap: 4px;
      min-width: 0;
      max-width: 100%;

      img {
        flex-shrink: 0;
      }
    }

    .invoice-code {
      min-width: 0;
      overflow-wrap: anywhere;
      word-break: break-all;
      white-space: normal;
      text-align: left;
    }

    // Row colors based on business type
    .el-table {
      .row-carsecurity {
        background-color: var(--color-carsecurity);
      }
      .row-secual {
        background-color: var(--color-secual);
      }
      .row-tokarika {
        background-color: var(--color-tokarika);
      }

      .row-carsecurity > td,
      .row-secual > td,
      .row-tokarika > td {
        background-color: inherit !important;
      }

      // Hover state
      .row-carsecurity:hover > td,
      .row-secual:hover > td,
      .row-tokarika:hover > td {
        background-color: inherit !important;
        filter: brightness(0.95);
      }
    }
  }
</style>
